import axios, { type AxiosResponse } from 'axios'
import type { CurrencyCode } from '@/constants/currencies'
import BaseService from '../BaseService'
import DashboardService from '../DashboardService'  // TODO: Remove cross-service dependency
import { require } from '@/utils/guards'

const PREMIUM_API = import.meta.env.VITE_PREMIUM_API_URL

// ==================== Type Definitions ====================

/** RateConnect campaign type */
export type RcCampaignType = 'NET_CPA' | 'CPA' | 'CPC'

/** Valid commission levels */
export type CommissionLevel = 12 | 15 | 18 | 20 | 25

/** Bidding goal/level */
export type BiddingGoal = 'low' | 'high' | 'auto' | 'boost'

/** AMS switch state */
export type SwitchState = 'ON' | 'OFF'

/** Date aggregation interval used by the statistics endpoint */
export type DateAggregationInterval = 'DAY' | 'WEEK' | 'MONTH'

/** Create RateConnect campaign body */
export interface CreateRateConnectCampaignBody {
  itemName: string
  campaignType?: RcCampaignType
  currencyCode: CurrencyCode
  partnerId: number
  cpa: CommissionLevel
  useAutomatedMarkets?: boolean
  language?: string
  lastName?: string
  firstName?: string
  budgetLimit?: number
}

/** Response from creating a RateConnect campaign */
export interface RateConnectCreateCampaignResponse {
  campaignType: RcCampaignType | null
}

/** Update bidding mode body */
export interface UpdateBiddingModeBody {
  biddingGoal: BiddingGoal
}

/** Switch AMS body */
export interface SwitchAMSBody {
  switchState: SwitchState
}

/** Update Net CPA campaign type body */
export interface UpdateNetCpaBody {
  commissionLevel: CommissionLevel
}

/** Accommodation request */
export interface AccommodationRequest {
  accommodationId: string
}

/** Create campaign request */
export interface CreateCampaignRequest {
  accommodationId: string
  body: CreateRateConnectCampaignBody
}

/** Expected top share request */
export interface ExpectedTopShareRequest {
  currentCpa: number
}

/** Pause campaign request */
export interface PauseCampaignRequest {
  accommodationId: string
  pauseUntil: string
}

/** Booking log filters */
export interface BookingLogFilters {
  accommodationId: string
  filtrationDate: 'bookingDate' | 'arrivalDate' | 'departureDate'
  currencyCode: CurrencyCode
  startDate: string
  endDate: string
  marketCodes?: string
  status?: string
}

/** A single booking log entry */
export interface BookingLogDTO {
  requestId: string
  bookingId: string | null
  marketCode: string
  bookingDate: string
  amountCampaignCurrency: number | null
  status: string | null
  commissionCost: number | null
  arrivalDate: string | null
  departureDate: string | null
  cancelledDate: string | null
  refundAmount: number | null
}

export interface BookingLogResponse {
  bookings: BookingLogDTO[]
  hasMoreBookings: boolean
}

/** Performance stats params */
export interface PerformanceStatsParams {
  accommodationId: string
  currencyCode: CurrencyCode
  aggregation: DateAggregationInterval
  startDate: string
  endDate: string
  marketCodes?: string
}

/** A single performance statistics data point */
export interface PerformanceStatisticsDTO {
  startDate: string | null
  endDate: string | null
  partnerClicks: number
  bookings: number
  bookingRevenue: number
  cancelledBookings: number | null
  commissionCost: number
  cpc: number | null
  cpa: number | null
  unavailabilityScore: number | null
}

export interface PerformanceStatisticsResponse {
  statistics: PerformanceStatisticsDTO[]
  hasMoreStatistics: boolean
  periodSummary: Record<string, any> | null
}

/** Partner markets request */
export interface PartnerMarketsRequest {
  accommodationId: string
  partnerId: number
}

/** A market available to a given partner */
export interface MarketDTO {
  marketCode: string | null
  marketName: string | null
  domain: string | null
  estimatedClicks: number
  hasCampaign: boolean | null
}

/** Market operation request */
export interface MarketOperationRequest {
  accommodationId: string
  marketCode: string
}

/** A single active market for a campaign */
export interface ActiveMarket {
  marketCode: string
  cpcLevel: string
  domain: string | null
}

export interface CompetitivenessStatisticsReferencePeriod {
  startDate: string
  endDate: string
}

/** Competitiveness statistics for an accommodation */
export interface CompetitivenessStats {
  accommodationId: number
  beatShare: number | null
  meetShare: number | null
  loseShare: number | null
  unavailabilityShare: number | null
  benchmark: number | null
  hotelImpressions: number | null
  referencePeriod: CompetitivenessStatisticsReferencePeriod
}

/** Update budget request */
export interface UpdateBudgetRequest {
  accommodationId: string
  budget: number
  currencyCode: CurrencyCode
}

/** Update bidding mode request */
export interface UpdateBiddingModeRequest {
  accommodationId: string
  body: UpdateBiddingModeBody,
}

/** Switch AMS request */
export interface SwitchAMSRequest {
  accommodationId: string
  body: SwitchAMSBody
}

/** KPIs request */
export interface KPIsRequest {
  accommodationId: string
  currencyCode: CurrencyCode
  marketCodes?: string
  startDate: string
  endDate: string
}

/** Performance KPIs response */
export interface PerformanceKpisResponse {
  startDate: string
  endDate: string
  visits: number | null
  bookings: number | null
  revenue: number | null
  cpa: number | null
  commissionCost: number | null
  boostedBookings: number | null
  boostedRevenue: number | null
}

/** Update campaign type request */
export interface UpdateCampaignTypeRequest {
  accommodationId: string
  body: UpdateNetCpaBody
}

/** Update commission level request */
export interface UpdateCommissionLevelRequest {
  accommodationId: string
  commissionLevel: number
}

/** A single commission level option */
export interface CommissionLevelOption {
  campaignType: number
  campaignTypeName: string
  commissionLevel: number
}

/** A single campaign record within a CampaignManagerResponse */
export interface CampaignRecord {
  campaignStatus: number
  localeCode: string
}

/** Response from campaign resume/pause/netcpa-update operations */
export interface CampaignManagerResponse {
  itemId: number
  partnerId: number
  campaigns: CampaignRecord[]
}

/** RateConnect campaign details */
export interface RateConnectCampaignResponse {
  partnerId: number | null
  status: string | null
  connectivityStatus: string
  cpa: number | null
  useAutomatedMarkets: boolean
  currencyCode: string | null
  campaignType: string | null
  endDate: string | null
}

/** Accommodation's RateConnect budget */
export interface Budget {
  limit: number
  used: number
  left: number
}

/** Budget recommendation request */
export interface BudgetRecommendationRequest {
  accommodationId: string
  currencyCode: CurrencyCode
  partnerId: number
  marketCodes?: string
}

export interface BudgetOutlook {
  low: number
  high: number
  boost: number
  auto: number
}

export interface BudgetRecommendationResponse {
  budgetMin: number
  budgetRecommendedMin: number
  budgetRecommendedMax: number
  expectedConsumption: BudgetOutlook | null
  weightedAverageCpc: number
}

/** Update partner request */
export interface UpdatePartnerRequest {
  itemId: string
  partnerId: number
}

/** Partner as returned by the accommodation-scoped partners endpoint */
export interface PartnerDTO {
  partnerId: number
  internalName: string
  canNetCpa: boolean | null
  partnerStatus: number
  connectionStatus: number
}

/** Partner as returned after updating an accommodation's partner */
export interface UpdatedPartnerDTO extends PartnerDTO {
  isCurrentPartner: boolean
}

/** Partner with name, composed client-side from PartnerDTO + GraphQL name lookup */
export interface PartnerWithName {
  canNetCpa?: boolean | null
  partnerId: number
  partnerStatus?: number
  connectionStatus?: number
  partnerName: string
}

/** Raw shape from GET /rateconnect/partners */
export interface PartnerBasicDetails {
  partnerId: number
  name: string
}

/** Simple partner, composed client-side */
export interface SimplePartner {
  partnerId: number
  partnerName: string
}

// ==================== Service Class ====================

class RateConnectService extends BaseService {
  constructor() {
    super({
      baseUrl: PREMIUM_API,
    })
  }

  createRateConnectCampaign({
    accommodationId,
    body
  }: { accommodationId: string; body: CreateRateConnectCampaignBody }): Promise<AxiosResponse<RateConnectCreateCampaignResponse>> {
    return axios({
      url: `${PREMIUM_API}/rateconnect/${accommodationId}/campaign`,
      method: 'POST',
      data: body,
      headers: this.studioHeadersWithAttribution(),
    })
  }

  /**
   * Validate RC voucher code for accommodation
   */
  validateRCVoucher({
    accommodationId,
    voucherCode,
    currencyCode,
  }: { accommodationId: string; voucherCode: string; currencyCode: CurrencyCode }): Promise<AxiosResponse> {
    if (!accommodationId) {
      throw new Error('Error in GET rateconnect/voucher: No accommodationId provided')
    }
    return axios({
      url: `${PREMIUM_API}/rateconnect/${accommodationId}/voucher`,
      method: 'GET',
      params: { voucherCode, currencyCode },
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Studio-Authorization': this.studioToken,
        ...this.getTrafficAttributionHeaders(),
      },
    })
  }

  /**
   * Get active markets for accommodation
   */
  getActiveMarkets({ accommodationId }: AccommodationRequest): Promise<AxiosResponse<ActiveMarket[]>> {
    return axios({
      url: `${PREMIUM_API}/rateconnect/${accommodationId}/markets`,
      method: 'GET',
      headers: this.studioHeadersWithAttribution(),
    })
  }

  getExpectedTopShare({ currentCpa }: ExpectedTopShareRequest): Promise<AxiosResponse> {
    return axios({
      url: `${PREMIUM_API}/rateconnect/opportunity/expected-top-share?currentCpa=${currentCpa}`,
      method: 'GET',
      headers: this.studioHeadersWithAttribution(),
    })
  }

  getCompetitivenessStats({ accommodationId }: AccommodationRequest): Promise<AxiosResponse<CompetitivenessStats>> {
    return axios({
      url: `${PREMIUM_API}/rateconnect/opportunity/${accommodationId}/competitiveness-stats`,
      method: 'GET',
      headers: this.studioHeadersWithAttribution(),
    })
  }

  getBoostOpportunities({ accommodationId }: AccommodationRequest): Promise<AxiosResponse> {
    return axios({
      url: `${PREMIUM_API}/rateconnect/${accommodationId}/boost/opportunities`,
      method: 'GET',
      headers: this.studioHeaders(),
    })
  }

  getCommissionLevels(): Promise<AxiosResponse<CommissionLevelOption[]>> {
    return axios({
      url: `${PREMIUM_API}/rateconnect/commission_levels`,
      method: 'GET',
      headers: this.studioHeadersWithAttribution(),
    })
  }

  resumeRateConnectCampaign({ accommodationId }: AccommodationRequest): Promise<AxiosResponse<CampaignManagerResponse>> {
    return axios({
      url: `${PREMIUM_API}/rateconnect/${accommodationId}/campaign/resume`,
      method: 'POST',
      headers: this.studioHeadersWithAttribution(),
    })
  }

  deactivateRateConnectCampaign({ accommodationId }: AccommodationRequest): Promise<AxiosResponse<CampaignManagerResponse>> {
    return axios({
      url: `${PREMIUM_API}/rateconnect/${accommodationId}/campaign/pause`,
      method: 'POST',
      headers: this.studioHeadersWithAttribution(),
    })
  }

  pauseRateConnectCampaign({ accommodationId, pauseUntil }: PauseCampaignRequest): Promise<AxiosResponse<CampaignManagerResponse>> {
    return axios({
      url: `${PREMIUM_API}/rateconnect/${accommodationId}/campaign/pause?pauseUntil=${pauseUntil}`,
      method: 'POST',
      headers: this.studioHeadersWithAttribution(),
    })
  }

  fetchBookingLog(filters: BookingLogFilters): Promise<AxiosResponse<BookingLogResponse>> {
    const { accommodationId, ...searchParams } = filters

    return axios({
      url: `${PREMIUM_API}/rateconnect/${accommodationId}/booking-log`,
      method: 'GET',
      params: searchParams,
      headers: this.studioHeadersWithAttribution(),
    })
  }

  fetchBookingsAmount(filters: Omit<BookingLogFilters, 'filtrationDate'>): Promise<AxiosResponse<BookingLogResponse>> {
    const { accommodationId, ...searchParams } = filters

    return axios({
      url: `${PREMIUM_API}/rateconnect/${accommodationId}/booking-log?filtrationDate=bookingDate`,
      method: 'GET',
      params: searchParams,
      headers: this.studioHeadersWithAttribution(),
    })
  }

  fetchCheckinData(filters: Omit<BookingLogFilters, 'filtrationDate'>): Promise<AxiosResponse<BookingLogResponse>> {
    const { accommodationId, ...searchParams } = filters

    return axios({
      url: `${PREMIUM_API}/rateconnect/${accommodationId}/booking-log?filtrationDate=arrivalDate`,
      method: 'GET',
      params: searchParams,
      headers: this.studioHeadersWithAttribution(),
    })
  }

  fetchCheckoutData(filters: Omit<BookingLogFilters, 'filtrationDate'>): Promise<AxiosResponse<BookingLogResponse>> {
    const { accommodationId, ...searchParams } = filters

    return axios({
      url: `${PREMIUM_API}/rateconnect/${accommodationId}/booking-log?filtrationDate=departureDate`,
      method: 'GET',
      params: searchParams,
      headers: this.studioHeadersWithAttribution(),
    })
  }

  fetchStats(params: PerformanceStatsParams): Promise<AxiosResponse<PerformanceStatisticsResponse>> {
    const { accommodationId, ...searchParams } = params

    return axios({
      url: `${PREMIUM_API}/rateconnect/${accommodationId}/performance/statistics`,
      method: 'GET',
      params: searchParams,
      headers: this.studioHeadersWithAttribution(),
    })
  }

  fetchMarkets({ accommodationId, partnerId }: PartnerMarketsRequest): Promise<AxiosResponse<MarketDTO[]>> {
    return axios({
      url: `${PREMIUM_API}/rateconnect/${accommodationId}/partners/${partnerId}/markets`,
      method: 'GET',
      headers: this.studioHeadersWithAttribution(),
    })
  }

  addMarket({ accommodationId, marketCode }: MarketOperationRequest): Promise<AxiosResponse<ActiveMarket>> {
    return axios({
      url: `${PREMIUM_API}/rateconnect/${accommodationId}/markets/${marketCode}`,
      method: 'POST',
      headers: this.studioHeadersWithAttribution(),
    })
  }

  removeMarket({ accommodationId, marketCode }: MarketOperationRequest): Promise<AxiosResponse<void>> {
    return axios({
      url: `${PREMIUM_API}/rateconnect/${accommodationId}/markets/${marketCode}`,
      method: 'DELETE',
      headers: this.studioHeadersWithAttribution(),
    })
  }

  exportBookingLogData(filters: BookingLogFilters): Promise<AxiosResponse<string>> {
    const { accommodationId, ...searchParams } = filters

    return axios({
      url: `${PREMIUM_API}/rateconnect/${accommodationId}/booking-log`,
      method: 'GET',
      params: searchParams,
      headers: {
        ...this.studioHeadersWithAttribution(),
        Accept: 'text/csv',
      },
    })
  }

  exportStatisticsData(params: PerformanceStatsParams): Promise<AxiosResponse<string>> {
    const { accommodationId, ...searchParams } = params

    return axios({
      url: `${PREMIUM_API}/rateconnect/${accommodationId}/performance/statistics`,
      method: 'GET',
      params: searchParams,
      headers: {
        ...this.studioHeadersWithAttribution(),
        Accept: 'text/csv',
      },
    })
  }

  /**
   * Fetch partners with names from GraphQL
   * TODO: Remove DashboardService dependency - merge GraphQL logic here
   */
  async fetchPartners({ accommodationId }: AccommodationRequest): Promise<PartnerWithName[]> {
    return axios<PartnerDTO[]>({
      url: `${PREMIUM_API}/rateconnect/${accommodationId}/partners`,
      method: 'GET',
      headers: this.studioHeadersWithAttribution(),
    }).then(async response => {
      const partnerIds: number[] = []
      response.data.map(item => partnerIds.push(item.partnerId))

      const partnersData = await DashboardService.fetchPartnersName(partnerIds)
      return response.data.map(item => {
        let name: string | undefined
        partnersData?.data?.data?.getAdvertiserDetails?.advertiserDetails?.forEach((partner: any) => {
          if (partner.nsid.id === item.partnerId) {
            name = partner.originalName.value
          }
        })
        return {
          canNetCpa: item.canNetCpa,
          partnerId: item.partnerId,
          partnerStatus: item.partnerStatus,
          connectionStatus: item.connectionStatus,
          partnerName: name ?? item.internalName,
        }
      })
    })
  }

  fetchNotifications({ accommodationId, scope }: { accommodationId: string; scope: string }): Promise<AxiosResponse> {
    require(scope)

    return axios({
      url: `${PREMIUM_API}/notifications/${accommodationId}?scopes=${scope}`,
      method: 'GET',
      headers: this.studioHeadersWithAttribution(),
    })
  }

  fetchCampaign({ accommodationId }: AccommodationRequest): Promise<AxiosResponse<RateConnectCampaignResponse>> {
    return axios({
      url: `${PREMIUM_API}/rateconnect/${accommodationId}/campaign`,
      method: 'GET',
      headers: this.studioHeadersWithAttribution(),
    })
  }

  updateCampaignBudget({ accommodationId, budget, currencyCode }: UpdateBudgetRequest): Promise<AxiosResponse<void>> {
    return axios({
      url: `${PREMIUM_API}/rateconnect/${accommodationId}/budget?currencyCode=${currencyCode}&budget=${budget}`,
      method: 'PUT',
      headers: this.studioHeadersWithAttribution(),
    })
  }

  updateBiddingMode({
    accommodationId,
    body
  }: { accommodationId: string; body: UpdateBiddingModeBody }): Promise<AxiosResponse<void>> {
    return axios({
      url: `${PREMIUM_API}/rateconnect/${accommodationId}/bidding`,
      method: 'PUT',
      data: body,
      headers: this.studioHeadersWithAttribution(),
    })
  }

  switchAMS({
    accommodationId,
    body
  }: { accommodationId: string; body: SwitchAMSBody }): Promise<AxiosResponse<void>> {
    return axios({
      url: `${PREMIUM_API}/rateconnect/${accommodationId}/ams`,
      method: 'PUT',
      data: body,
      headers: this.studioHeadersWithAttribution(),
    })
  }

  getAccommodationBudget(accommodationId: string): Promise<AxiosResponse<Budget>> {
    return axios({
      url: `${PREMIUM_API}/rateconnect/${accommodationId}/budget`,
      method: 'GET',
      headers: this.studioHeadersWithAttribution(),
    })
  }

  fetchKPIS(request: KPIsRequest): Promise<AxiosResponse<PerformanceKpisResponse>> {
    const { accommodationId, ...searchParams } = request

    return axios({
      url: `${PREMIUM_API}/rateconnect/${accommodationId}/performance/kpis`,
      method: 'GET',
      params: searchParams,
      headers: this.studioHeadersWithAttribution(),
    })
  }

  updateCampaignType({
    accommodationId,
    body
  }: { accommodationId: string; body: UpdateNetCpaBody }): Promise<AxiosResponse<CampaignManagerResponse>> {
    return axios({
      url: `${PREMIUM_API}/rateconnect/${accommodationId}/campaign/netcpa`,
      method: 'PUT',
      data: body,
      headers: this.studioHeadersWithAttribution(),
    })
  }

  updateCommissionLevel({
    accommodationId,
    commissionLevel
  }: { accommodationId: string; commissionLevel: CommissionLevel }): Promise<AxiosResponse<void>> {
    return axios({
      url: `${PREMIUM_API}/rateconnect/${accommodationId}/commission_levels/${commissionLevel}`,
      method: 'PUT',
      headers: this.studioHeadersWithAttribution(),
    })
  }

  fetchRecommendation(request: BudgetRecommendationRequest): Promise<AxiosResponse<BudgetRecommendationResponse>> {
    const { accommodationId, ...params } = request

    return axios({
      url: `${PREMIUM_API}/rateconnect/${accommodationId}/budget-recommendation`,
      method: 'GET',
      params,
      headers: this.studioHeadersWithAttribution(),
    })
  }

  /**
   * Check rate availability (Yoda live check) for accommodation
   */
  checkRateAvailability({ accommodationId }: AccommodationRequest): Promise<AxiosResponse> {
    if (!this.studioToken) {
      throw new Error(`Error in GET rateconnect/${accommodationId}/rate-check: No studioToken provided`)
    }

    return axios({
      url: `${PREMIUM_API}/rateconnect/${accommodationId}/rate-check`,
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Studio-Authorization': this.studioToken,
        ...this.getTrafficAttributionHeaders(),
      },
    })
  }

  updatePartner({ itemId, partnerId }: UpdatePartnerRequest): Promise<AxiosResponse<UpdatedPartnerDTO>> {
    return axios({
      url: `${PREMIUM_API}/rateconnect/${itemId}/campaign/partner/${partnerId}`,
      method: 'PUT',
      headers: this.studioHeadersWithAttribution(),
    })
  }

  /**
   * Fetch all available partners with names from GraphQL
   * TODO: Remove DashboardService dependency - merge GraphQL logic here
   */
  async fetchAllPartners(): Promise<SimplePartner[]> {
    return axios<PartnerBasicDetails[]>({
      url: `${PREMIUM_API}/rateconnect/partners`,
      method: 'GET',
      headers: this.studioHeadersWithAttribution(),
    }).then(async response => {
      const partnerIds: number[] = []
      response.data.map(item => partnerIds.push(item.partnerId))

      const partnersData = await DashboardService.fetchPartnersName(partnerIds)

      return response.data.map(item => {
        let name: string | undefined
        partnersData?.data?.data?.getAdvertiserDetails?.advertiserDetails?.forEach((partner: any) => {
          if (partner.nsid.id === item.partnerId) {
            name = partner.originalName.value
          }
        })

        return {
          partnerId: item.partnerId,
          partnerName: name ?? item.name,
        }
      })
    })
  }
}

export default new RateConnectService()
